import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Typography from 'common/components/typography';
import theme from 'common/theme';
import event from '~helpers/mixpanel';
import { handleTimeDuration, APITOKEN_TIME, DEPOSITE_TIME, COUNTEROFFER_TIME } from './common';
import AboutPropertyAndBooking from './property-booking';
import { BoardNotice } from 'common/components/svgs';
import { useSelector } from 'react-redux';
import ShowMoreModal from './showMore';
import useStyles from './style';
import { getSafeHtml } from 'common/helpers/safeHtml';
import { pageType } from '~constants/pages';

const Broadcast = ({ landlord }) => {
  const classes = useStyles();

  const { t } = useTranslation();

  const [visible, setVisible] = useState(false);

  const pendingAt = landlord?.company?.pendingAt;

  const verifiedAt = landlord?.company?.verifiedAt;

  let contentArr = [
    {
      key: 'pendingAtContent',
      contentType: 'News',
      content: {
        title: t('landlord.new_dashboard.broadcast.qualification_is_being_verified'),

        link: '/',
      },
      time: pendingAt,
    },
    {
      key: 'verifyAtContent',
      contentType: 'News',
      content: {
        title: t('landlord.new_dashboard.broadcast.qualification_is_verified'),
      },
      time: verifiedAt,
    },
    {
      key: 'apiTokenAtContent',
      contentType: 'Release',
      content: {
        desc: t('landlord.new_dashboard.broadcast.api_token'),

        link: '/help-center/api-token',
      },
      time: APITOKEN_TIME,
    },
    {
      key: 'paymentPlanAtContent',
      contentType: 'Release',
      content: {
        desc: t('landlord.new_dashboard.broadcast.Payment_Plan'),
        link: '/help-center/payment-plan',
      },
      time: DEPOSITE_TIME,
    },
    {
      key: 'counterOfferAtContent',
      contentType: 'Release',
      content: {
        desc: t('landlord.new_dashboard.broadcast.counter_offer_notice'),
        link: '/help-center/counter-offer',
      },
      time: COUNTEROFFER_TIME,
    },
  ];

  const handleClickViewBusinessInfo = () => {
    event('Clicked Dashboard Page', {
      'Clicked Position': 'View Business Info',
      'Current Page Type': pageType.dashboard,
    });
  };

  const handleClickLearnMore = () => {
    event('Clicked News Feed Btn', {
      'Clicked Position': 'Learn More',
      'Current Page Type': pageType.dashboard,
    });

    event('Viewed News in Help Center Page', {
      'Auth Method': 'Dashboard Page',
      'Current Page Type': pageType.dashboard,
    });
  };

  const { isPropertyLevel } = useSelector(state => ({
    isPropertyLevel: state.auth.getIn(['base', 'accountLevel']) === 'PROPERTY',
  }));

  const ReleaseEle = ({ desc, link, time }) => {
    return (
      <>
        <div style={{ marginBottom: '4px' }}>
          <BoardNotice className={classes.icon} />
          <span className={classes.secondsTitle}>
            {t('landlord.new_dashboard.show_more.modal.func.release.title')}
          </span>
        </div>
        <div className={classes.contentWrapper}>
          <Typography
            className={classes.content}
            fontSize={14}
            lineHeight={22}
            themeColor={theme.colors.gray9}
          >
            <span>{desc}</span>
            <span className={classes.learnMore}>
              <Link to={link} target="_blank" onClick={handleClickLearnMore}>
                {t('landlord.new_dashboard.broadcast.learn_more')}
              </Link>
            </span>
          </Typography>
          <Typography
            className={classes.date}
            themeColor={theme.colors.gray7}
            fontSize={12}
            lineHeight={22}
          >
            {handleTimeDuration(time, t)}
          </Typography>
        </div>
      </>
    );
  };

  const NewsEle = ({ time, title, link }) => {
    return (
      <If condition={time}>
        <div className={classes.contentWrapper}>
          <Typography
            className={classes.content}
            fontSize={14}
            lineHeight={22}
            themeColor={theme.colors.gray9}
          >
            <span>{getSafeHtml(title)}</span>
            <If condition={link}>
              <Link to={link} onClick={handleClickViewBusinessInfo}>
                {t('landlord.new_dashboard.broadcast.view_business_info')}
              </Link>
            </If>
          </Typography>
          <Typography
            className={classes.date}
            themeColor={theme.colors.gray7}
            fontSize={12}
            lineHeight={22}
          >
            {handleTimeDuration(time, t)}
          </Typography>
        </div>
      </If>
    );
  };

  const sortContent = arr => {
    const computeTimeStamp = arr => {
      arr.forEach(item => {
        item.timeStamp = item.time ? new Date(item.time).getTime() : 0;
      });
      return arr;
    };
    const deleteApiToken = arr => {
      const index = arr.findIndex(i => i.key === 'apiTokenAtContent');
      arr.splice(index, 1);
      return arr;
    };
    const sortByTimeStamp = arr => {
      return arr.sort((a, b) => b.timeStamp - a.timeStamp).slice(0, 3);
    };

    let newContentArr = computeTimeStamp(arr);
    if (isPropertyLevel) {
      newContentArr = deleteApiToken(newContentArr);
    }
    return sortByTimeStamp(newContentArr);
  };

  const contents = sortContent(contentArr);

  return (
    <div className={classes.broadcastWrapper}>
      <div className={classes.broadCastBox}>
        <Typography className={classes.blankContent} themeColor={theme.colors.gray9}>
          {t('landlord.new_dashboard.broadcast.title')}
          <span
            className={classes.showMore}
            onClick={() => {
              event('Clicked News Feed Btn', {
                'Clicked Position': 'Show More',
                'Current Page Type': pageType.dashboard,
              });
              setVisible(true);
            }}
          >
            {t('landlord.new_dashboard.broadcast.show_more')}
          </span>
        </Typography>
        <For each="item" of={contents} index="index">
          <If condition={item.contentType === 'News'}>
            <NewsEle {...item.content} time={item.time} />
          </If>
          <If condition={item.contentType === 'Release'}>
            <ReleaseEle {...item.content} time={item.time} />
          </If>
        </For>
      </div>
      <ShowMoreModal
        visible={visible}
        landlord={landlord}
        cancel={() => {
          setVisible(false);
        }}
      />
      <AboutPropertyAndBooking />
    </div>
  );
};

Broadcast.propTypes = {
  landlord: PropTypes.shape({
    company: PropTypes.shape({
      pendingAt: PropTypes.string,
      verifiedAt: PropTypes.string,
    }),
  }),
};

export default Broadcast;
